import { UnifiedItem } from "../../../explorer/src/types/data";
import {
  BUILD_ORDER_GROUPING_INTERVAL,
  COMBINE_MAX_STEPS_AGO,
  COMBINE_SIMILAIR_BUILD_STEPS_MARGIN,
  COMBINE_STEPS,
  GAME_CIV_MAP,
  PLAYER_COLORS,
} from "../config";
import { getItemFromAction, getItemFromIcon, getItemFromPbgid } from "../itemMapping";
import { formatSecondsAsDuration, sumPerInterval } from "./parseUtils";
import { GameScores, GameSummary, ReplayData, ResourceType, SummaryResponse } from "./types";

const pendingRequests = new Map<string, Promise<any>>();
const cache = new Map();
/** Request a json file and deduplicate requests to single promises, optionally stored in cache */
export async function fetchJson<T = any>(url: string, useCache = false): Promise<T> {
  if (useCache && cache.has(url)) return cache.get(url);
  if (pendingRequests.has(url)) return pendingRequests.get(url);
  const request = new Promise<T>(async (resolve, reject) => {
    try {
      const response = await fetch(url);
      if (response.ok) {
        try {
          const data = await response.json();
          if (useCache || cache.has(url)) cache.set(url, data);
          resolve(data);
        } catch {
          reject(new Error("Error parsing response format (not JSON)"));
        } finally {
          pendingRequests.delete(url);
        }
      } else {
        reject(new Error(`${response.status} ${response.statusText}`));
      }
    } catch (e) {
      reject(new Error(`Error requesting data from ${url}: ${e.message}`));
    }
  });

  pendingRequests.set(url, request);
  return request;
}

function assignColors(players: ReplayData[]) {
  const teams = [...new Set(players.map((p) => p.team))];
  const colorSet =
    teams.length == 2 ? PLAYER_COLORS.TWO_TEAMS : teams.length == 3 || teams.length == 4 ? PLAYER_COLORS.TWO_TEAMS : PLAYER_COLORS.FFA_OR_UNKNOWN;
  return Object.fromEntries(
    players.map(
      (p, i) =>
        [
          p.profileId,
          colorSet == PLAYER_COLORS.FFA_OR_UNKNOWN
            ? colorSet[i]
            : colorSet[p.team]?.[players.filter((pf) => pf.team == p.team).indexOf(p)] ?? PLAYER_COLORS.FFA_OR_UNKNOWN[i],
        ] as [number, string]
    )
  );
}

export async function getGameSummary(url: string): Promise<GameSummary[]> {
  const replayData = await fetchJson<SummaryResponse>(url);
  if (replayData.error) throw new Error(replayData.error);

  const summaries: GameSummary[] = [];
  for(const p of replayData.players) {
    // fallback to snakecase to camelCase mappings
    p.profileId ??= p.profile_id
    p.buildOrder ??= p.build_order
    p.resources.foodPerMin ??= p.resources.food_per_min;
    p.resources.woodPerMin ??= p.resources.wood_per_min;
    p.resources.goldPerMin ??= p.resources.gold_per_min;
    p.resources.stonePerMin ??= p.resources.stone_per_min;
    p.resources.oliveoilPerMin ??= p.resources.oliveoil_per_min;
  }

  const colors = assignColors(replayData.players);
  const issues: string[] = [];
  for (const replay of replayData.players) {
    const scores: Partial<GameScores> = {};
    let steps: GameSummary["steps"] = [];
    const items: GameSummary["items"] = [];
    const { buildOrder, resources, name, profileId, scores: _scores, _stats = {}, actions } = replay;
    const position = replayData.players.indexOf(replay) + 1;
    const team = replay.team;
    const teamName = replay.teamName;
    const civ = GAME_CIV_MAP[replay.civilization];
    if (!civ) throw new Error(`Could not match civ '${replay.civilization}' for player ${name ?? replayData.players.indexOf(replay) + 1}`);

    for (const g of buildOrder) {
      let item: UnifiedItem;
      if (g.type == "Unit" && g.unknown?.["14"]) g.finished.push(...g.unknown["14"]);
      if (g.type == "Unit" && g.unknown?.["15"]) g.destroyed.push(...g.unknown["15"]);
      const spawned = [...g.finished, ...g.constructed];

      try {
        item = await getItemFromPbgid(g.pbgid, civ);
        if (item === undefined) {
          item = await getItemFromIcon(g.icon, civ);
        }
        if (item === undefined) {
          const msg = `Could not find item ${g.pbgid} ${g.icon} for ${civ.name}, occuring at ${spawned.sort()?.map(formatSecondsAsDuration).join(", ")}`;
          issues.push(msg);
          continue;
        }
        if (item === null) continue;
      } catch (e) {
        console.warn(e);
        issues.push(e.message);
        continue;
      }

      steps.push(...spawned.map((s) => ({ item, timestamp: s })));
      const stat = items.find((s) => s.item.id == item.id) ?? items[items.push({ item, counts: { build: 0, lost: 0 }, build: [], lost: [] }) - 1];
      stat.counts.build += spawned.length;
      stat.counts.lost += g.destroyed.length;
      stat.build.push(...spawned);
      stat.lost.push(...g.destroyed);
    }

    if ([GAME_CIV_MAP.mongols, GAME_CIV_MAP.ottomans, GAME_CIV_MAP.malians].includes(civ)) {
      /* For mongols we parse out build orders for research from actions
      /* we see if we have a matching technology for the action
      /* and if so, we replace/remove the existing build order for that research with this one.
      /* Specifically, for improved research the 'non-improved' icon spawns, so if you first research
      /* the vanilla version and later the improved, the same icon so same build order occurs twice.
      /* We remove the vanilla version completely and insert the vanilla research matched from the action,
      /* and if the improved action is there, add it too.
      /* One edge case is when researching the improved version and skipping the vanilla, in that case we
      /* check if the vanilla research exists once (spawned for the improved version), in which case we remove it too
      */
      for (const [key, timestamps] of Object.entries(actions)) {
        const item = await getItemFromAction(key, civ);
        if (!item) continue;
        const stat = items.find((s) => s.item.id == item.id) ?? items[items.push({ item, counts: { build: 0, lost: 0 }, build: [], lost: [] }) - 1];
        steps = steps.filter((s) => s.item.id != item.id && (s.item.id != item.id.replace("-improved", "") || stat.counts.build == 1));
        steps.push(...timestamps.map((timestamp) => ({ item, timestamp })));

        stat.counts = { lost: 0, build: timestamps.length };
        stat.build = [...timestamps];
      }
    }

    steps.sort((a, b) => a.timestamp - b.timestamp);
    items.sort((a, b) => b.counts.build - a.counts.build);

    const groupedSteps = steps.reduce<GameSummary["groupedSteps"]>((acc, step, i) => {
      const lastSimilairStep = acc
        .slice(-1 * COMBINE_MAX_STEPS_AGO)
        .reverse()
        .find((a) => a.item.id === step.item.id);
      if (
        COMBINE_STEPS &&
        (lastSimilairStep?.timestamp > step.timestamp - COMBINE_SIMILAIR_BUILD_STEPS_MARGIN ||
          (lastSimilairStep && acc.at(-1) == lastSimilairStep && lastSimilairStep.timestamp > step.timestamp - BUILD_ORDER_GROUPING_INTERVAL))
      ) {
        lastSimilairStep.count++;
        lastSimilairStep.timestamps.push(step.timestamp);
      } else acc.push({ ...step, count: 1, timestamps: [step.timestamp] });

      return acc;
    }, []);

    const resourceTypes: ResourceType[] = ["food", "gold", "stone", "wood", "oliveoil"];

    let resourceSpent = replay.totalResourcesSpent;
    let resourceGathered = replay.totalResourcesGathered;

    if (!resourceSpent || !resourceGathered)
    {
      // Old parsed replay doesn't have stats yet, use approximation of resources gatherd based on total resources spent and resources remaining at the end of the game
      resourceSpent = items.reduce(
        (acc, { counts, item }) => {
          if (!item.variations?.length) return acc;
          const costs = item.variations[0].costs;
          for (const type of resourceTypes) acc[type] += counts.build * (costs[type] || 0);
          return acc;
        },
        { food: 0, wood: 0, gold: 0, stone: 0, oliveoil: 0, total: 0 }
      );
      resourceSpent.total = resourceTypes.reduce((acc, type) => acc + resourceSpent[type], 0);
      const approximateResourceGathered = Object.fromEntries(resourceTypes.map((type) => [type, (resources[type]?.at(-1) || 0) + resourceSpent[type]])) as Record<
        ResourceType,
        number
      >;
      resourceGathered = { ...approximateResourceGathered, total: resourceTypes.reduce((acc, type) => acc + approximateResourceGathered[type], 0) };
    }

    const maxItemsBuild = (id: string) => {
      const item = items.find((i) => i.item.id == id);
      return item ? Math.max(...sumPerInterval(item, resources.timestamps)) : 0;
    };

    const kdRatio = (kills: number, deaths: number) => {
      if (deaths)
        return Math.round((kills / deaths) * 100) / 100;
      else if (kills)
        return '∞';
      else
        return '';
    }

    Object.assign(scores, {
      totalResourcesGathered: resourceGathered.total,
      totalWoodGathered: resourceGathered.wood,
      totalFoodGathered: resourceGathered.food,
      totalGoldGathered: resourceGathered.gold,
      totalStoneGathered: resourceGathered.stone,
      totalOliveOilGathered: resourceGathered.oliveoil,

      totalResourcesSpent: resourceSpent.total,
      totalWoodSpent: resourceSpent.wood,
      totalFoodSpent: resourceSpent.food,
      totalGoldSpent: resourceSpent.gold,
      totalStoneSpent: resourceSpent.stone,
      totalOliveOilSpent: resourceSpent.oliveoil,

      apm: replay.apm,

      workersMax: maxItemsBuild("villager") || maxItemsBuild("dragon-villager"),
      tradersMax: maxItemsBuild("trader") || maxItemsBuild("trade-caravan"),
      fishingMax: Math.max(maxItemsBuild("fishing-boat"), maxItemsBuild("lodya-fishing-boat")),
      workersIdleTime: _stats["inactperiod"],

      sacredSitesCaptured: _stats["pcap"],
      sacredSitesLost: _stats["plost"],
      sacredSitesNeutralized: _stats["precap"],

      militaryBuilt: _stats["sqprod"],
      kills: _stats["elitekill"],
      deaths: _stats["edeaths"],
      killsPerDeath: kdRatio(_stats["elitekill"] || 0, _stats["edeaths"] || 0),

      buildingsRazed: _stats["structdmg"],
      buildingsLost: _stats["blost"],

      researchProduced: _stats["upg"],

      total: _scores.total,
      military: _scores.military,
      economy: _scores.economy,
      technology: _scores.technology,
      society: _scores.society,
    });

    summaries.push({
      name,
      items,
      steps,
      groupedSteps,
      resources,
      scores,
      civ,
      profileId,
      position,
      team,
      teamName,
      color: colors[profileId],
      issues,
    });
  }

  return summaries;
}
